import React, { useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
// import { name, random } from 'faker';
import gql from 'graphql-tag';
import { FetchPolicy } from 'apollo-client';
import { TextField, CircularProgress } from '@material-ui/core';
import { RenderInputParams } from '@material-ui/lab';
import { useDebouncedCallback } from 'use-debounce/lib';
import { InfiniteAutocomplete } from '../../components/InfiniteAutocomplete';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { client } from '../..';
import { showNotification } from '../../App';

// "Armstrong, Kristian" -> "KA"; "Kristian Armstrong" -> "KA"
export const getInitials = (name?: string) => {
    if (!name) {
        return '';
    }
    const [lastName, firstName] = name.includes(',') ? name.split(',').map((part) => part.trim()) : [name.trim().split(' ').slice(-1)[0], name.trim().split(' ')[0]];
    return `${(firstName || '').charAt(0)}${(lastName || '').charAt(0)}`.toUpperCase();
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        textField: {
            width: '100%',
        },
        iconSelected: {
            width: 17,
            height: 17,
            marginRight: 5,
            marginLeft: -2,
            },
            color: {
                width: 14,
                height: 14,
                flexShrink: 0,
                borderRadius: 3,
                marginRight: 8,
                marginTop: 2,
            },
            text: {
                flexGrow: 1,
            },
            close: {
                opacity: 0.6,
                width: 18,
                height: 18,
            },
            renderWrapper: {
                padding: '15px 0'
            },
            customRenderWrapper: {
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                minWidth: 0,
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                '& .avatar': {
                    flex: '0 0 auto',
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                    backgroundColor: '#eef0f6',
                    color: '#667085',
                },
                // a RedView user: the accent colours, so the people who can sign in stand out from contacts without an account
                '& .avatar.redview-user': {
                    backgroundColor: 'rgba(22, 103, 199, 0.12)',
                    color: '#1667c7',
                },
                '& .other': {
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexFlow: 'column',
                },
                '& .top': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                },
                '& .primary': {
                    flex: 1,
                    minWidth: 0,
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
                '& .role': {
                    flex: '0 0 auto',
                    maxWidth: '45%',
                    padding: '1px 8px',
                    borderRadius: 999,
                    backgroundColor: '#f2f4fa',
                    color: '#667085',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
                '& .secondary': {
                    color: 'rgba(0, 0, 0, 0.54)',
                    fontSize: '0.8125rem',
                    lineHeight: 1.4,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
            // the open list is as wide as the names need (the field is narrow), without a sideways scrollbar
            root: {
                minWidth: '200px',
                '& .MuiAutocomplete-popper': {
                    width: 'auto !important',
                    minWidth: 340,
                },
                '& .MuiAutocomplete-listbox': {
                    overflowX: 'hidden',
                },
            },
    })
);

interface UserAssistantSelectorProps {
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
    isActive?: boolean;
    multiple?: boolean;
    label?: string;
    name: string;
    required?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    // tslint:disable-next-line: no-any
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[] | any, name: string) => void;
    id?: string;
    visible?: {
        role: boolean;
    };
    error?: boolean;
    disablePortal?: boolean;
    className?: string;
    helperText?: React.ReactNode;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
}

interface UserAssistantSelectorState {
    hasNextPage: boolean;
    isNextPageLoading: boolean;
    offset: number;
    filter: string;
    // tslint:disable-next-line: no-any
    items: any;
}

export const UserAssistantSelector: React.FC<UserAssistantSelectorProps> = props => {

    const classes = useStyles();
    const loadBlockLimit = 20;

    const [state, setState] = useState<UserAssistantSelectorState>({
        hasNextPage: true,
        isNextPageLoading: false,
        offset: 0,
        filter: '',
        items: [],
    });

    const [delayedonInputChange] = useDebouncedCallback(
        // function
        (event: React.ChangeEvent<{}>, value: string, reason: 'input' | 'reset' | 'clear') => {

            let isClear = false;
            let offset = 0;
            let filter = '';

            if (reason === 'input') {
                filter = value;
                isClear = true;

                fetchData(filter, offset, isClear);
            }
        },
        // delay in ms
        500
    );

    const fetchData = (filter: string, offset: number, isClear: boolean) => {

        setState((prevState) => {
            return {
                ...prevState,
                isNextPageLoading: true
            };
        });

        const userQueryParams: UserStaffQueryParams = {
            filter: filter,
            first: loadBlockLimit,
            offset: offset,
            isActive: props.isActive
        };

        retrieveUserListData(
            userQueryParams,
            false, // this.state.isRefresh, -- Since we are updating and creating, we do not fetch cached data
            // tslint:disable-next-line: no-console
            (data) => onRetrieveUserList(data, filter, offset, isClear),
            // tslint:disable-next-line:no-any
            function (reason: any): void {
                showNotification(null, reason, 'error');

                setState((prevState) => {
                    return {
                        ...prevState,
                        isNextPageLoading: false,
                    };
                });
            }
        );
    };

    const onRetrieveUserList = (data: UserListData, filter: string, offset: number, isClear: boolean) => {

        const userData = data.feeEarnerDashboardFeeEarner.feeEarner;
        const recordCount = data.feeEarnerDashboardFeeEarner.recordCount;

        // tslint:disable-next-line: no-any
        let itemsList: any = [];

        let items = state.items;

        if (!isClear) {
            itemsList = [...state.items];
        } else {
            items = [];
        }

        if (items.length === 0) {
            itemsList = userData.map( (source: FeeEarner) => ({
                ...source,
                value: source.staffId, 
                label: source.staffName,
                email: source.emailAddress,
                isRedViewUser: source.isRedViewUser,
                entityType: source.entityType,
                roleName: source.staffRole
            }));
        } else {
            for (const user of userData) {
                
                // tslint:disable-next-line: no-any
                const isExists = items.filter((item: any) => item.emailAddress.toLowerCase().includes(user.emailAddress)).length;

                if (isExists === 0) {
                    itemsList.push({
                        ...user,
                        label: user.staffName,
                        value: user.staffId,
                        isRedViewUser: user.isRedViewUser,
                        email: user.emailAddress,
                        entityType: user.entityType,
                        roleName: user.staffRole
                    });
                }
            }
        }

        setState((prevState) => {
            return {
                ...prevState,
                filter: filter,
                isNextPageLoading: false,
                items: itemsList,
                offset: offset + loadBlockLimit,
                hasNextPage: userData.length !== 0 && (offset + loadBlockLimit) < recordCount,
                isLoading: false
            };
        });

    };

    // tslint:disable-next-line: no-any
    const loadNextPage = (...args: any) => {

        if (state.hasNextPage && !state.isNextPageLoading) {
            fetchData(state.filter, state.offset, false);
        }
    };

    const onClose = (event: React.ChangeEvent<{}>) => {

        setState((prevState) => {
            return {
                ...prevState,
                isNextPageLoading: false,
                hasNextPage: true,
                filter: '',
                offset: 0,
                items: []
            };
        });
    };

    const onOpen = (event: React.ChangeEvent<{}>) => {
        fetchData(state.filter, 0, true);
    };

    // tslint:disable-next-line:no-any
    const onSelection = (selection: IAutoCompleteItem | any, name: string) => {

        if (props.onSelection) {
            props.onSelection(selection, name);
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return ({ 
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                    {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                    {params.InputProps.endAdornment}
                </React.Fragment>
                ), 
            });
    };

    return (
        <div className={`${classes.root} ${props.className}`}>
            <InfiniteAutocomplete
    size={props.size ? props.size : 'medium'}
                id={props.id}
                itemSize={58}
                loading={state.isNextPageLoading}
                disablePortal={props.disablePortal !== undefined ? props.disablePortal : true}
                name={props.name}
                isMultiple={props.multiple}
                value={props.value}
                hasNextPage={state.hasNextPage}
                isNextPageLoading={state.isNextPageLoading}
                items={state.items}
                loadNextPage={loadNextPage}
                // disableCloseOnSelect={true}
                className={classes.textField}
                disableListWrap={true}
                onSelection={onSelection}
                onClose={onClose}
                onOpen={onOpen}
                getOptionLabel={(option: IAutoCompleteItem) => option.label}
                getOptionSelected={(option, value) => value.value === option.value}
                onInputChange={delayedonInputChange}
                onBlur={props.onBlur}
                renderOption={(option, { selected }) => {
                    return (
                        <div className={classes.customRenderWrapper}>
                            <div className={`avatar${option.isRedViewUser ? ' redview-user' : ''}`} title={option.isRedViewUser ? 'RedView user' : undefined}>
                                {getInitials(option.label)}
                            </div>
                            <div className="other">
                                <div className="top">
                                    <div className="primary" title={option.label}>{option.label}</div>
                                    {option.roleName && <div className="role" title={option.roleName}>{option.roleName}</div>}
                                </div>
                                <div className="secondary" title={option.email}>{option.email}</div>
                            </div>
                        </div>
                    );
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant={props.variant ? props.variant : 'standard'}
                        size={props.size ? props.size : 'medium'}
                        label={props.label}
                        required={props.required}
                        error={props.error}
                        fullWidth={true}
                        InputProps={getInputProps(params, state.isNextPageLoading)}
                        helperText={props.helperText}
                    />
                )}
            />
        </div>
    );
};

export function retrieveUserListData(
    query: UserStaffQueryParams,
    refreshData: boolean,
    onSuccess: (data: UserListData) => void,
    // tslint:disable-next-line:no-any
    onError: (reason: any) => void
): void {
    var fetchPolicy: FetchPolicy = refreshData === true ? 'network-only' : 'cache-first';
    client
        .query({
            query: FEE_EARNER_QUERY,
            variables: {
                filter: query.filter,
                first: query.first,
                offset: query.offset,
                isActive: query.isActive,
            },
            fetchPolicy: fetchPolicy
        })
        // tslint:disable-next-line:no-any
        .then((results: { data: any }) => {
            onSuccess(results.data);
        })
        // tslint:disable-next-line:no-any
        .catch((reason: any) => {
            onError(reason);
        });
}

export interface UserStaffQueryParams {
    offset?: number; 
    first?: number;
    filter?: string;
    isActive?: boolean;
}

export interface UserListData {
    loading?: boolean;
    networkStatus?: number;
    feeEarnerDashboardFeeEarner: FeeEarners;
}

export interface FeeEarners {
    recordCount: number;
    feeEarner: FeeEarner[];
}

export interface FeeEarner {
    staffId: string;
    staffName: string;
    emailAddress: string;
    staffRole: string;
    isRedViewUser: boolean;
    entityType: number;
}

const FEE_EARNER_QUERY = gql`
    query feeEarnerDashboardFeeEarner($offset: Int, $first: Int, $filter: String, $isActive: Boolean) {
        feeEarnerDashboardFeeEarner(offset: $offset, first: $first, filter: $filter, isActive: $isActive){
            recordCount,
            feeEarner{
                staffId,
                staffName,
                emailAddress,
                isRedViewUser,
                isActive,
                staffRole
            }
        }
    }
`;
